import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { navigation } from '../lib/navigation'
import { isConsole } from '../lib/surface'
import { api } from '../lib/api'
import { useToast } from '../lib/toast'
import { useApi } from '../lib/useApi'
import { useTheme } from '../lib/theme'
import { SHORTCUTS_EVENT } from './Shortcuts'

interface Command {
  id: string
  label: string
  kind: string
  run: () => void
}

/**
 * ⌘K navigation. Developers running a dozen services should not have to
 * click through a list to reach one, and the palette is the one place where
 * services and databases are searchable together.
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const ref = useRef<HTMLDialogElement>(null)
  const navigate = useNavigate()
  const { theme, toggle } = useTheme()
  const { attempt } = useToast()

  // Only fetched while the palette is open, so it costs nothing at rest.
  const { data: services } = useApi(
    () => (!isConsole && open ? api.services() : Promise.resolve([])),
    [open],
  )
  const { data: databases } = useApi(
    () => (!isConsole && open ? api.databases() : Promise.resolve([])),
    [open],
  )

  const { data: projects, error: projectError } = useApi(() => !isConsole && open ? api.projects() : Promise.resolve([]), [open])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOpen((current) => !current)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) {
      setQuery('')
      setCursor(0)
      dialog.showModal()
    }
    if (!open && dialog.open) dialog.close()
  }, [open])

  const commands = useMemo<Command[]>(() => {
    const go = (path: string) => () => {
      setOpen(false)
      navigate(path)
    }

    const list: Command[] = [
      ...navigation(isConsole).map(item => ({ id: `nav-${item.to}`, label: item.label, kind: 'go', run: go(item.to) })),
      ...(!isConsole ? [{id: 'nav-new', label: 'New application', kind: 'create', run: go('/services/new')}] : []),
      {
        id: 'shortcuts',
        label: 'Keyboard shortcuts',
        kind: 'help',
        run: () => {
          setOpen(false)
          window.dispatchEvent(new Event(SHORTCUTS_EVENT))
        },
      },
      {
        id: 'theme',
        label: `Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`,
        kind: 'toggle',
        run: () => {
          toggle()
          setOpen(false)
        },
      },
    ]

    // An action closes the palette immediately and reports through a toast:
    // waiting on a request with the palette still up would feel stuck.
    const act = (label: string, fn: () => Promise<unknown>, success: string) => ({
      run: () => {
        setOpen(false)
        void attempt(fn, { success, failure: `Could not ${label}` })
      },
    })

    for (const project of projects ?? []) {
      list.push({ id: `project-${project.id}`, label: project.name, kind: 'application', run: go(`/projects/${project.id}`) })
    }
    for (const service of services ?? []) {
      list.push({
        id: `svc-${service.id}`,
        label: service.name,
        kind: 'service',
        run: go(`/services/${service.id}`),
      })
      list.push({
        id: `deploy-${service.id}`,
        label: `Deploy application containing ${service.name}`,
        kind: 'action',
        ...act(
          'deploy',
          async () => {
            const release = await api.deployApplication(service.id)
            navigate(`/projects/${release.project_id}`)
          },
          `Deploying ${service.name}`,
        ),
      })
      list.push({
        id: `restart-${service.id}`,
        label: `Restart ${service.name}`,
        kind: 'action',
        ...act(
          'restart',
          () => api.restart(service.id),
          `${service.name} is restarting`,
        ),
      })
      list.push(
        service.status === 'suspended'
          ? {
              id: `resume-${service.id}`,
              label: `Resume ${service.name}`,
              kind: 'action',
              ...act('resume', () => api.resume(service.id), `${service.name} is starting`),
            }
          : {
              id: `suspend-${service.id}`,
              label: `Suspend ${service.name}`,
              kind: 'action',
              ...act(
                'suspend',
                () => api.suspend(service.id),
                `${service.name} is suspended`,
              ),
            },
      )
    }
    for (const database of databases ?? []) {
      list.push({
        id: `db-${database.id}`,
        label: database.name,
        kind: 'database',
        run: go(`/databases/${database.id}`),
      })
    }
    return list
  }, [services, databases, projects, navigate, theme, toggle, attempt])

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return commands.slice(0, 8)
    return commands
      .filter((command) => command.label.toLowerCase().includes(needle))
      .slice(0, 10)
  }, [commands, query])

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setCursor((current) => Math.min(current + 1, matches.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setCursor((current) => Math.max(current - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      matches[cursor]?.run()
    }
  }

  return (
    <dialog
      className="palette"
      ref={ref}
      onCancel={() => setOpen(false)}
      onClose={() => setOpen(false)}
      aria-label="Command palette"
    >
      <div className="palette-box">
        <input
          className="palette-input"
          placeholder={isConsole ? "Search console pages…" : "Find an application, component or action…"}
          value={query}
          autoFocus
          onChange={(event) => {
            setQuery(event.target.value)
            setCursor(0)
          }}
          onKeyDown={onKeyDown}
          aria-label="Search" role="combobox" aria-expanded={open} aria-controls="command-results"
          aria-autocomplete="list" aria-activedescendant={matches[cursor] ? `command-${matches[cursor].id}` : undefined}
        />
        {projectError && <p className="tiny muted" role="status">Application search is unavailable. You can still open workspace pages.</p>}
        <div className="palette-list" role="listbox" id="command-results" aria-label="Commands">
          {matches.length === 0 && (
            <div className="palette-item muted">Nothing matches “{query}”.</div>
          )}
          {matches.map((command, index) => (
            <button
              key={command.id} id={`command-${command.id}`} tabIndex={-1}
              className="palette-item"
              role="option"
              aria-selected={index === cursor}
              onMouseEnter={() => setCursor(index)}
              onClick={command.run}
            >
              <span className="truncate">{command.label}</span>
              <span className="palette-kind">{command.kind}</span>
            </button>
          ))}
        </div>
      </div>
    </dialog>
  )
}

